import { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils } from '@fortawesome/free-solid-svg-icons';
import { getCategoryPhotoObject } from '../../../lib/unsplash';
import UnsplashImage from '../../UnsplashImage';
import ClapButton from '../../shared/ClapButton';

const RecipeCard = memo(function RecipeCard({ recipe }) {
  const navigate = useNavigate();
  const [photoObject, setPhotoObject] = useState(recipe.unsplashPhoto || null);
  const [loading, setLoading] = useState(!recipe.unsplashPhoto);

  useEffect(() => {
    // If recipe already has unsplashPhoto, use it
    if (recipe.unsplashPhoto) {
      setPhotoObject(recipe.unsplashPhoto);
      setLoading(false);
      return;
    }

    // Otherwise, fetch photo object
    if (recipe.imageSearchQuery || recipe.title) {
      setLoading(true);
      getCategoryPhotoObject('recipes', recipe, 0).then(photo => {
        setPhotoObject(photo);
        setLoading(false);
      });
    }
  }, [recipe]);

  const handleCardClick = (e) => {
    // Don't navigate if clicking on clap button
    if (e.target.closest('button')) {
      return;
    }
    navigate(`/explore/recipe/${recipe.id}`);
  };

  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className="group bg-white rounded-2xl border border-border overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-primary/10 to-accent/10 overflow-hidden">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
          </div>
        ) : recipe.unsplashPhoto ? (
          <div className="w-full h-full">
            <UnsplashImage
              photo={recipe.unsplashPhoto}
              alt={recipe.title}
              className="group-hover:scale-110 transition-transform duration-500 w-full h-full object-cover"
              onClick={handleCardClick}
              triggerDownloadOnClick={true}
            />
          </div>
        ) : recipe.uploadedImageUrl ? (
          <div className="relative w-full h-full">
            <img
              src={recipe.uploadedImageUrl}
              alt={recipe.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              onClick={handleCardClick}
            />
            {recipe.imageCaption && (
              <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs text-white">
                {recipe.imageCaption}
              </div>
            )}
          </div>
        ) : photoObject ? (
          <div className="w-full h-full">
            <UnsplashImage
              photo={photoObject}
              alt={recipe.title}
              className="group-hover:scale-110 transition-transform duration-500 w-full h-full object-cover"
              onClick={handleCardClick}
              triggerDownloadOnClick={true}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FontAwesomeIcon icon={faUtensils} className="text-6xl text-primary/20" />
          </div>
        )}
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Badge overlay */}
        <div className="absolute top-3 left-3">
          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-text-primary">
            Recipe
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-serif font-bold text-lg text-text-primary mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {recipe.title}
        </h3>

        <p className="text-sm text-text-secondary line-clamp-2 mb-4">
          {recipe.description}
        </p>

        {/* Meta info */}
        <div className="flex items-center gap-4 text-xs text-text-secondary pb-4 border-b border-border">
          {recipe.difficulty && (
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {recipe.difficulty}
            </div>
          )}
          {recipe.time && (
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {recipe.time}
            </div>
          )}
          {recipe.serves && (
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Serves {recipe.serves}
            </div>
          )}
        </div>

        {/* Claps and View */}
        <div className="mt-4 flex items-center justify-between">
          {recipe.id && (
            <ClapButton 
              contentId={recipe.id} 
              contentType="recipe" 
              size="sm"
              showCount={true}
            />
          )}
          
          <span className="text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            View recipe
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </motion.div>
  );
});

export default RecipeCard;

